import React, { useRef, useState, useEffect } from 'react';
import './loginPermission.css';
import { useDispatch, useSelector } from 'react-redux';
import SettingsSheet from '../settingSheet/SettingsSheet.js';

function C2wLoginPermission() {

    const dispatch = useDispatch()

    const settingFlag = useSelector((state) => state.data.settingFlag)


    /**
 * @constant stdData
 * @author Vishal Lohbande
 *
 * Description:
 * - Initializes a state object `stdData` using React’s `useState` hook.
 * - Holds data fields related to a student or form input:
 *    - `date`: (string) typically a date value, initialized empty.
 *    - `name`: (string) student’s name, initialized empty.
 *    - `number`: (string) contact number or ID, initialized empty.
 *    - `reason`: (string) reason or purpose text, initialized empty.
 *    - `course`: (string) course name or identifier, initialized empty.
 *
 * Usage:
 * - Used to store and update form or student-related data within a React component.
 */
    const [stdData, setStdData] = useState({
        date: '',
        name: "",
        number: "",
        reason: "",
        course: "",
    });

    /**
 * @function handleChange
 * @author Vishal Lohbande
 *
 * Description:
 * - Handles input change events for form elements.
 * - Updates the `stdData` state by merging the existing state with the new key-value pair.
 * - Uses the `name` attribute of the input (`e.target.name`) as the key.
 * - Uses the input’s current value (`e.target.value`) as the new value for that key.
 *
 * Usage:
 * - Designed to be used as an `onChange` handler for controlled form inputs.
 * - Enables dynamic updates to multiple fields in the `stdData` state object.
 *
 * Example:
 * ```jsx
 * <input name="name" value={stdData.name} onChange={handleChange} />
 * ```
 *
 * Notes:
 * - Assumes the event target has a `name` attribute matching keys in `stdData`.
 */
    const handleChange = (e) => {
        setStdData({ ...stdData, [e.target.name]: e.target.value });
    };

    /**
 * @function handleNumberChange
 * @author Vishal Lohbande
 *
 * Description:
 * - Handles input changes specifically for the `number` field in `stdData`.
 * - Limits the input length to a maximum of 10 characters.
 * - Logs to console if the input length exceeds 10 and prevents state update.
 * - If input length is 10 or fewer, updates the `number` field in `stdData` state.
 *
 * Usage:
 * - Used as an `onChange` handler for number input fields to enforce length constraints.
 *
 * Notes:
 * - Uses `console.log` for debugging input length and control flow.
 * - Prevents user from entering more than 10 characters in the `number` field.
 *
 * Example:
 * ```jsx
 * <input name="number" value={stdData.number} onChange={handleNumberChange} />
 * ```
 */
    const handleNumberChange = (e) => {
        console.log(e.target.value.length > 10)
        if (e.target.value.length > 10) {
            console.log('return')
            return
        } else {
            setStdData({
                ...stdData,
                number: e.target.value
            })
        }

    }

    /*function to check the provided mobile no is valid or not*/
    /**
 * @function isValidMobile
 * @author Vishal Lohbande
 *
 * Description:
 * - Validates whether a given input string is either a valid Indian mobile number or a valid email address.
 * - Uses regular expressions to test the input:
 *   - `mobileRegex`: Checks for a 10-digit Indian mobile number starting with digits 6-9.
 *   - `emailRegex`: Checks for a general email format.
 * - Returns `true` if the input matches either pattern, otherwise returns `false`.
 *
 * Usage:
 * - Used to validate user input that accepts either a mobile number or an email address.
 *
 * Notes:
 * - The mobile number regex is specific to Indian phone numbers.
 * - The email regex is a basic pattern and might not cover all valid email cases.
 *
 * Example:
 * ```js
 * isValidMobile("9876543210"); // true
 * isValidMobile("user@example.com"); // true
 * isValidMobile("12345"); // false
 * ```
 */
    const isValidMobile = (input) => {
        const mobileRegex = /^[6-9]\d{9}$/; // Indian mobile number pattern (10 digits, starts with 6-9)
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/; // General email pattern

        if (mobileRegex.test(input)) {
            return true;
        } else if (emailRegex.test(input)) {
            return true;
        } else {
            return false;
        }
    };

    const [loader, setLoader] = useState(false); //to handle button loader
    const [error, setError] = useState(false); // to handle error
    const [inValidNumber, setInValidNumber] = useState(false); //to number validation
    const [popup, setPopup] = useState(false);

    /**
     * Handles the form submission for student data.
     * 
     * This async function validates the user input, shows a loading spinner, 
     * prevents default form behavior, and sends the form data to a Google Apps Script endpoint.
     * 
     * Steps performed:
     * - Validates if all required fields are filled.
     * - Validates the mobile number format.
     * - Displays error messages if validation fails.
     * - Sends form data using a POST request to an external API (Google Sheet).
     * - Resets form data and state after successful submission.
     * - Displays a popup for 3 seconds to confirm successful submission.
     * 
     * @param {Event} e - The form submission event.
     * 
     * @returns {Function|void} A cleanup function to clear timeout, or nothing.
     * 
     */
    const handleSubmit = async (e) => {
        e.preventDefault(); // ✅ Move to the top
        setError(false);
        setInValidNumber(false);
    
        // Show loader only if name exists (optional logic)
        if (stdData.name) setLoader(true);
    
        // Validate fields
        const isInvalid = [
            stdData.name,
            stdData.number,
            stdData.reason,
            stdData.course
        ].some(field => !field); // Simplified check for '', null, or undefined
    
        if (isInvalid) {
            setError(true);
            setLoader(false);
            return;
        }
    
        if (!isValidMobile(stdData.number)) {
            setLoader(false);
            setInValidNumber(true);
            return;
        }
    
        // Prepare form data
        const formData = new FormData();
        formData.append('date', new Date());
        Object.keys(stdData).forEach(key => {
            formData.append(key, stdData[key]);
        });
    
        try {
            const response = await fetch('https://script.google.com/macros/s/AKfycbyWla4wq-ge77WZNyUVaB6D2TAV_jBMsTUVnwSrkfQTFPL3qGxYNlgyvrqyazaZ4CiO/exec', {
                method: 'POST',
                body: formData
            });
    
            if (!response.ok) throw new Error('Network error or script issue');
    
            // Reset on success
            setLoader(false);
            setStdData({
                date: '',
                name: "",
                number: "",
                reason: "",
                course: "",
            });
            setPopup(true);
    
            // Auto close popup
            const timer = setTimeout(() => {
                setPopup(false);
            }, 3000);
    
            return () => clearTimeout(timer);
        } catch (error) {
            console.error(error);
            alert("Not Submitted, Please Try Again");
            setLoader(false);
        }
    };
    

    return (
        <div className='C2wLoginPermission_ctn'>
            <div className='C2wLoginPermission_ctn_right'>
                <div className='C2wLoginPermission_heading'>{'Request admin for login permission'}</div>
                <table className='C2wLoginPermission_table'>
                    <tr><input className='C2wLoginPermission_table_input' placeholder='Enter Your Name'
                        type="text"
                        name="name"
                        onChange={handleChange}
                        value={stdData.name}
                        style={{ borderColor: error && stdData?.name === '' ? 'red' : null }}
                    /></tr>
                    <tr><input className='C2wLoginPermission_table_input' placeholder='Enter Mobile Number'
                        type="text"
                        name="number"
                        value={stdData.number}
                        onChange={handleNumberChange}
                        style={{ borderColor: inValidNumber || (error && stdData?.number === '') ? 'red' : null }}

                    /></tr>
                    <tr><input className='C2wLoginPermission_table_input' placeholder='Enter Logout Reason (Auto Logout, Mobile Change, New Device)'
                        type="text"
                        name="reason"
                        value={stdData.reason}
                        onChange={handleChange}
                        style={{ borderColor: error && stdData?.reason === '' ? 'red' : null }}
                    /></tr>
                    <tr><input className='C2wLoginPermission_table_input' placeholder='Enter Your Course'
                        type="text"
                        name="course"
                        value={stdData.course}
                        onChange={handleChange}
                        style={{ borderColor: error && stdData?.course === '' ? 'red' : null }}
                    /></tr>
                    <tr><button className='C2wLoginPermission_table_submitbtn' onClick={!loader ? handleSubmit : null}>{loader ? <div className='btnLoader'></div> : "Submit"}</button></tr>
                </table>{
                    popup ? <div className='C2wrequest_popup'>
                        <div className='C2wrequest_popup_container'>
                            <svg width="100" height="94" viewBox="0 0 100 94" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="49.0815" cy="46.0102" r="32.0883" fill="#00B64C" />
                                <path d="M60.983 36.3818C60.8251 36.2039 60.6372 36.0627 60.4301 35.9663C60.223 35.8699 60.001 35.8203 59.7766 35.8203C59.5523 35.8203 59.3302 35.8699 59.1232 35.9663C58.9161 36.0627 58.7282 36.2039 58.5702 36.3818L45.9115 50.5421L40.5932 44.5818C40.4292 44.4049 40.2356 44.2657 40.0234 44.1723C39.8113 44.0789 39.5848 44.0331 39.3568 44.0375C39.1288 44.0419 38.9038 44.0964 38.6947 44.198C38.4855 44.2995 38.2963 44.4461 38.1379 44.6293C37.9795 44.8125 37.8549 45.0288 37.7713 45.2658C37.6877 45.5028 37.6467 45.7558 37.6507 46.0105C37.6546 46.2652 37.7034 46.5166 37.7943 46.7502C37.8852 46.9838 38.0164 47.1952 38.1804 47.3721L44.7051 54.6611C44.8631 54.839 45.051 54.9802 45.2581 55.0766C45.4651 55.1729 45.6872 55.2225 45.9115 55.2225C46.1359 55.2225 46.3579 55.1729 46.565 55.0766C46.7721 54.9802 46.96 54.839 47.1179 54.6611L60.983 39.1721C61.1555 38.9943 61.2932 38.7786 61.3873 38.5385C61.4815 38.2984 61.5301 38.0391 61.5301 37.7769C61.5301 37.5148 61.4815 37.2555 61.3873 37.0154C61.2932 36.7753 61.1555 36.5595 60.983 36.3818Z" fill="white" />
                                <circle cx="13.778" cy="7.87368" r="7.87368" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="93.1719" cy="15.749" r="5.90526" fill="#00B64C" fill-opacity="0.2" />
                                <ellipse cx="3.93684" cy="69.8744" rx="3.93684" ry="3.60877" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="34.777" cy="91.2028" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="85.2985" cy="83.3356" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="89.235" cy="58.3903" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                                <circle cx="56.4274" cy="3.93717" r="1.96842" fill="#00B64C" fill-opacity="0.2" />
                            </svg>
                            <h1>Your Login permission request has been sent to the admin</h1>
                        </div>
                    </div> : null
                }

            </div>
        </div>
    )
}

export default C2wLoginPermission
